'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { Enrollment } from './learningSource';
import type { ChannelCard, MediaItem } from './mediaSource';

/**
 * Host adapter contract for per-user data consumed by the user-state
 * islands (Wishlist button, WatchLater button, Subscribe button,
 * Watch progress, Course enrollment, Review composer, Continue
 * Learning, Watch History, etc.).
 *
 * <p>Implementation reality: partially backed by our LMS backend
 * (course wishlist, enrollment progress, completed certificates) and
 * partially by Cinema8 (watch progress, watch-later, subscribed
 * channels, watch history). The contract abstracts both behind one
 * adapter so islands don't have to know which backend serves which
 * field.
 *
 * <h3>Auth handling</h3>
 *
 * Hosts return {@code null} (or throw a typed
 * {@link UserResourceAnonymousError}) when the request is made by an
 * anonymous user. The {@code useUserResource} hook on the consumer
 * side already distinguishes anonymous vs error states — adapter
 * implementations should mirror that by surfacing anonymous via
 * {@code null}, real failures via thrown {@code Error}.
 *
 * <p>Namespaced by domain. Each namespace is OPTIONAL — hosts whose
 * backend doesn't yet model a domain omit it. Consuming islands
 * gracefully fall back to "sign in to track progress" placeholders
 * when the namespace they need is absent.
 */

/** Thrown by adapter methods to signal "user is not authenticated"
 *  in a way the islands' anonymous-fallback path can detect. Hosts
 *  may also return {@code null} from list methods; throwing is the
 *  unambiguous signal for boolean / mutation methods. */
export class UserResourceAnonymousError extends Error {
  constructor() {
    super('Anonymous user');
    this.name = 'UserResourceAnonymousError';
  }
}

/** Watch progress for a single video. */
export interface VideoProgress {
  videoSlug: string;
  positionSeconds: number;
  durationSeconds: number;
  /** Pre-computed by the backend (positionSeconds/durationSeconds). */
  progressPercent: number;
  completed?: boolean;
  lastWatchedAt: string;
}

/** Toggle response. {@code skipped: true} indicates anonymous user
 *  (mutation rejected). */
export interface ToggleResult {
  active?: boolean;
  skipped?: boolean;
}

// =============================================================================
// Namespaces
// =============================================================================

/** Course wishlist — "save for later" on course cards. Backend:
 *  {@code PUT /v1/employee/bookmarks/course/favourites/{id}}. */
export interface WishlistNamespace {
  list(signal?: AbortSignal): Promise<{ courseSlugs: string[] } | null>;
  has(courseSlug: string, signal?: AbortSignal): Promise<boolean | null>;
  toggle(args: { courseSlug: string }): Promise<ToggleResult | null>;
}

/** Course-level "watch later" — distinct from {@link WatchLaterNamespace},
 *  which is Cinema8's VIDEO queue keyed by videoSlug. This one is the
 *  LMS backend's course bookmark: {@code PUT
 *  /v1/employee/bookmarks/course/watch-later/{id}}. Same shape as
 *  {@link WishlistNamespace} since it's the same kind of per-course
 *  toggle, just a different bookmark type. */
export interface CourseWatchLaterNamespace {
  list(signal?: AbortSignal): Promise<{ courseSlugs: string[] } | null>;
  has(courseSlug: string, signal?: AbortSignal): Promise<boolean | null>;
  toggle(args: { courseSlug: string }): Promise<ToggleResult | null>;
}

/** Video watch-later — backed by Cinema8. */
export interface WatchLaterNamespace {
  list(signal?: AbortSignal): Promise<{ items: MediaItem[]; skipped: boolean }>;
  has(videoSlug: string, signal?: AbortSignal): Promise<boolean | null>;
  toggle(args: { videoSlug: string }): Promise<ToggleResult | null>;
}

/** Video likes (thumbs-up) — backed by Cinema8's toggle-like op.
 *  Mirrors {@link WatchLaterNamespace} minus the list read: the
 *  upstream has no self-scoped "my likes" feed the portal can call
 *  without a personAccessId, and the video-data detail carries a
 *  like COUNT but no per-user liked flag — so {@code has} returns
 *  {@code null} (unknown → button starts unpressed) rather than
 *  guessing. */
export interface LikesNamespace {
  has(videoSlug: string, signal?: AbortSignal): Promise<boolean | null>;
  toggle(args: { videoSlug: string }): Promise<ToggleResult | null>;
  /** The signed-in viewer's liked videos (for the Liked library tab). */
  list(signal?: AbortSignal): Promise<{ items: MediaItem[]; skipped: boolean }>;
}

/** Channel subscriptions — backed by Cinema8. */
export interface SubscriptionsNamespace {
  list(signal?: AbortSignal): Promise<{ items: ChannelCard[]; skipped: boolean }>;
  isSubscribed(channelSlug: string, signal?: AbortSignal): Promise<boolean | null>;
  toggle(args: { channelSlug: string }): Promise<ToggleResult | null>;
}

/** Watch-progress per video — backed by Cinema8. */
export interface WatchProgressNamespace {
  fetch(videoSlug: string, signal?: AbortSignal): Promise<VideoProgress | null>;
  update(args: {
    videoSlug: string;
    positionSeconds: number;
    durationSeconds?: number;
    completed?: boolean;
  }): Promise<VideoProgress | null>;
  /** Reset to zero ("watch again"). */
  reset(args: { videoSlug: string }): Promise<boolean>;
}

/** Watch history (recently watched videos). */
export interface HistoryNamespace {
  list(opts?: {
    limit?: number;
    signal?: AbortSignal;
  }): Promise<{ items: MediaItem[]; skipped: boolean }>;
  /** Fire-and-forget marker — usually written by the player itself
   *  via {@code MediaSource.library.markHistory}; included here for
   *  hosts that want to write history from non-Media surfaces. */
  mark(args: { videoSlug: string }): Promise<void>;
  clear(): Promise<boolean>;
}

/** Course progress — last-played lesson, completion status. Mirrors
 *  the {@link Enrollment} shape from LearningSource for hosts whose
 *  course-progress lives on a different table than enrollment
 *  records. Re-uses the {@code Enrollment} type so consumers can
 *  reason about both surfaces uniformly. */
export interface CourseProgressNamespace {
  fetch(courseSlug: string, signal?: AbortSignal): Promise<Enrollment | null>;
}

/** Personalized recommendations — courses/videos picked by the host's
 *  recommender. Optional because not every host runs one. */
export interface RecommendationsNamespace {
  fetch(opts?: {
    kind?: 'course' | 'video' | 'mixed';
    limit?: number;
    signal?: AbortSignal;
  }): Promise<MediaItem[]>;
}

/** Course reviews — fetch + write. */
export interface ReviewsNamespace {
  fetchMine(
    courseSlug: string,
    signal?: AbortSignal,
  ): Promise<{
    rating: number;
    text?: string;
    createdAt: string;
  } | null>;
  submit(args: { courseSlug: string; rating: number; text?: string }): Promise<boolean>;
}

export interface MyCourseItem {
  courseId: string | number;
  courseTitle: string;
  courseDescription?: string;
  courseThumbnail?: string;
  courseSlug?: string;
  courseStartDate?: number;
  courseEndDate?: number;
  startDate?: number;
  completionDate?: number;
  attendeeStatus?: string;
  approvalStatus?: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  completionPercentage?: number;
  expired?: boolean;
  pendingPostTest?: boolean;
  pluginId?: string;
  /** `notes` mode only — the note record's own id (distinct from
   *  {@link courseId}, its parent course). Needed for delete/download. */
  noteId?: string | number;
  /** `notes` mode only — the note body text. */
  noteText?: string;
  /** `notes` mode only — when the note was taken (epoch millis). */
  noteDate?: number;
  /** `notes` mode only — the category picked in the player (summary / key-points / questions). */
  noteType?: string;
}

export interface ApprovalApprover {
  name?: string;
  surname?: string;
  admin?: boolean;
  approvalStatus?: string;
  weight?: number;
  reason?: { description?: string };
}

export interface ApprovalDetail {
  id?: number;
  approvalEntityType?: string;
  approvalEntityId?: number | string;
  approvalStatus?: string;
  approvedEntity?: {
    title?: string;
    learningType?: string;
    imageUrl?: string;
  };
  approvers?: ApprovalApprover[];
}

/** My courses list — filterable by mode (in_progress, assigned, completed, etc.). */
export interface MyCoursesNamespace {
  fetch(opts: {
    mode: string;
    q?: string;
    courseType?: string;
    attendeeStatus?: string;
    approvalStatus?: string;
    /** `notes` mode only — narrows to one note category. */
    noteType?: string;
    /** `notes` mode only — narrows to one course's notes (a program's covers all its sub-courses). */
    noteCourseId?: string | number;
    firstResult?: number;
    pageSize?: number;
    signal?: AbortSignal;
  }): Promise<{ items: MyCourseItem[]; total?: number }>;
  fetchApprovalDetail(entityId: string | number, language?: string): Promise<ApprovalDetail>;
  /** `notes` mode only — deletes a single note by its own id ({@link MyCourseItem.noteId}). */
  deleteNote?(noteId: string | number): Promise<void>;
  /** `notes` mode only — downloads the note(s) as a PDF blob. Pass a
   *  `courseId` to scope to a single course's note; omit for all notes. */
  downloadNotesPdf?(courseId?: string | number): Promise<Blob>;
}

/** A pending form the employee must fill in before/around a course
 *  (e.g. a post-course evaluation survey). */
export interface PendingFormItem {
  /** The form record's own id — used to build the player deep-link. */
  id: string | number;
  courseId: string | number;
  courseTitle: string;
  courseThumbnail?: string;
}

/** Pending-forms inbox — e.g. evaluation forms awaiting the employee. */
export interface PendingFormsNamespace {
  fetch(opts: {
    formType: 'EVALUATION' | (string & {});
    signal?: AbortSignal;
  }): Promise<PendingFormItem[]>;
}

/** A single out-of-plan / non-planned learning request submitted by
 *  the employee, awaiting manager approval. */
export interface DemandFormItem {
  id: number | string;
  title?: string;
  courseType?: 'F2F_COURSE' | 'REMOTE_COURSE' | 'PROGRAM' | (string & {});
  approvalStatus?: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  courseDate?: string | number;
  category?: { id: number | string; name?: string };
  country?: string;
  city?: string;
  cost?: number;
  currency?: { id: number | string; name?: string };
  duration?: number;
  timeUnit?: 'HOUR' | 'DAY' | 'WEEK' | (string & {});
  reason?: string;
  purpose?: string;
  sourceType?: 'INTERNAL' | 'EXTERNAL' | (string & {});
  referenceUrl?: string;
}

/** Category option offered in the "new request" form's category
 *  picker. Flattened (parent + children) by the host adapter. */
export interface DemandFormCategory {
  id: number | string;
  label: string;
}

/** Currency option offered in the "new request" form's currency
 *  picker (paged search, keyed by name). */
export interface DemandFormCurrency {
  id: number | string;
  name: string;
  symbolLeft?: string;
  symbolRight?: string;
  decimalPlaces?: number;
}

export interface DemandFormNewRequest {
  title: string;
  courseType: string;
  sourceType: 'INTERNAL' | 'EXTERNAL' | (string & {});
  categoryId: number | string;
  reason: string;
  purpose: string;
  referenceUrl?: string;
  courseDate?: string;
  duration: number;
  timeUnit: string;
  cost: number;
  currencyId: number | string;
  country?: string;
  city?: string;
}

/** The employee who filed a request — surfaced in the approval detail. */
export interface DemandFormEmployee {
  id?: number | string;
  name?: string;
  middleName?: string;
  surname?: string;
  avatarURL?: string;
}

/** Full detail of a single request + its approval chain — backs the
 *  "Açıklama" (description) modal opened from the request table. */
export interface DemandFormApprovalDetail {
  id?: number | string;
  approvalStatus?: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  approvedEntity?: DemandFormItem & { employee?: DemandFormEmployee };
  approvers?: ApprovalApprover[];
}

/** Out-of-plan / non-planned training request list + submission +
 *  approval detail. Backs the DemandFormBlock. */
export interface DemandFormNamespace {
  search(opts: {
    firstResult?: number;
    maxResults?: number;
    approvalStatus?: string;
    title?: string;
    signal?: AbortSignal;
  }): Promise<{ items: DemandFormItem[]; total?: number }>;
  submit(request: DemandFormNewRequest): Promise<{ ok: boolean; duplicate?: boolean }>;
  fetchCategories(signal?: AbortSignal): Promise<DemandFormCategory[]>;
  /** Paged currency picker, filterable by name. */
  fetchCurrencies(opts: {
    firstResult?: number;
    maxResults?: number;
    name?: string;
    signal?: AbortSignal;
  }): Promise<{ items: DemandFormCurrency[]; total?: number }>;
  fetchDetail(entityId: number | string, signal?: AbortSignal): Promise<DemandFormApprovalDetail>;
}

/** One saved billing/shipping address. Mirrors the LMS backend's
 *  {@code /v1/geo/address} entity (the same one `/profile-settings`'s
 *  address tab and akademi-lms-v2's checkout address picker both use). */
export interface UserAddress {
  id: string | number;
  name?: string;
  addressLine1?: string;
  phoneNumber?: string;
  city?: string;
  district?: string;
  country?: string;
}

/** Saved addresses — akademi-lms-v2's cart checkout address step reads
 *  these via a generic (non-marketplace-scoped) employee address search;
 *  {@code MarketplaceAddressSelect} uses this namespace instead of
 *  {@code MarketplaceSource} for that reason. Backend: {@code POST
 *  /v1/geo/address/search}. */
export interface AddressesNamespace {
  list(signal?: AbortSignal): Promise<UserAddress[]>;
}

// =============================================================================
// Adapter contract
// =============================================================================

export interface UserResourceSource {
  wishlist?: WishlistNamespace;
  watchLater?: WatchLaterNamespace;
  likes?: LikesNamespace;
  courseWatchLater?: CourseWatchLaterNamespace;
  subscriptions?: SubscriptionsNamespace;
  watchProgress?: WatchProgressNamespace;
  history?: HistoryNamespace;
  courseProgress?: CourseProgressNamespace;
  recommendations?: RecommendationsNamespace;
  reviews?: ReviewsNamespace;
  myCourses?: MyCoursesNamespace;
  pendingForms?: PendingFormsNamespace;
  demandForm?: DemandFormNamespace;
  addresses?: AddressesNamespace;
}

const UserResourceSourceContext = createContext<UserResourceSource | null>(null);

export function UserResourceSourceProvider({
  value,
  children,
}: {
  value: UserResourceSource;
  children: ReactNode;
}) {
  return (
    <UserResourceSourceContext.Provider value={value}>
      {children}
    </UserResourceSourceContext.Provider>
  );
}

export function useUserResourceSource(): UserResourceSource | null {
  return useContext(UserResourceSourceContext);
}
